import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { CalendarDays, Clock, Download, FileCheck2, NotebookPen } from "lucide-react";
import PageHeader from "@/components/shared/PageHeader";
import StatusChip from "@/components/shared/StatusChip";
import SectionCard from "@/components/shared/SectionCard";
import StatCard from "@/components/shared/StatCard";
import EmptyState from "@/components/shared/EmptyState";
import { SearchInput } from "@/components/shared/SearchInput";
import { TableSkeleton } from "@/components/shared/Skeletons";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { formatDate } from "@/utils/formatters";
import { useAdminWorkLogs } from "@/hooks/useAdminData";
import { useAuthStore } from "@/store/authStore";

import ActivityCalendar, { CalendarAsideCard } from "@/components/shared/ActivityCalendar";
import { latestLogs, relativeDay, workLogEvents, workLogLegend } from "@/components/shared/workLogCalendar";
import { toneForStatus } from "@/components/shared/EventChip";

export const Route = createFileRoute("/admin/work-logs/")({
  component: AdminWorkLogs,
});

// The API names the people on a log student_name / partner_name (with camelCase
// aliases), not student / partner. Reading the short keys left every real row
// reading "Unknown Student" / "Unknown Partner".
function studentNameOf(log: any): string {
  return log.student_name || log.studentName || log.student || "Unknown Student";
}

function partnerNameOf(log: any): string {
  return log.partner_name || log.partnerName || log.partner || "Unknown Partner";
}

const REVIEWED_STATUSES = ["approved", "rejected", "changes_requested"];

function AdminWorkLogs() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [partnerFilter, setPartnerFilter] = useState("all");
  const { user } = useAuthStore();

  const { data: workLogsData = [], isLoading } = useAdminWorkLogs();

  const displayLogs = workLogsData;

  const totalHours = displayLogs.reduce((sum: number, log: any) => sum + (log.hours || 0), 0);

  const reviewedLogs = displayLogs.filter((l: any) => REVIEWED_STATUSES.includes(l.status));
  const approvedLogs = reviewedLogs.filter((l: any) => l.status === "approved");
  const approvalRate =
    reviewedLogs.length > 0 ? Math.round((approvedLogs.length / reviewedLogs.length) * 100) : null;

  // Review latency is the average time between a log being written and a
  // supervisor deciding on it. Logs that have not been reviewed are excluded.
  const latencyDays = (() => {
    const durations = displayLogs
      .filter((l: any) => l.reviewed_at && l.created_at)
      .map(
        (l: any) =>
          (new Date(l.reviewed_at).getTime() - new Date(l.created_at).getTime()) /
          (1000 * 60 * 60 * 24),
      )
      .filter((d: number) => Number.isFinite(d) && d >= 0);
    if (durations.length === 0) return null;
    return durations.reduce((a: number, b: number) => a + b, 0) / durations.length;
  })();

  const partnerOptions = Array.from(
    new Set(
      displayLogs
        .map((l: any) => partnerNameOf(l))
        .filter((name: string) => name && name !== "Unknown Partner"),
    ),
  ).sort() as string[];

  const filteredLogs = displayLogs.filter((log: any) => {
    const student = studentNameOf(log);
    const partner = partnerNameOf(log);
    const matchesSearch =
      student.toLowerCase().includes(search.toLowerCase()) ||
      partner.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === "all" || log.status === statusFilter;
    const matchesPartner = partnerFilter === "all" || partnerNameOf(log) === partnerFilter;
    return matchesSearch && matchesStatus && matchesPartner;
  });

  const qualityValue =
    user?.isDemo && approvalRate === null ? "94%" : approvalRate === null ? "—" : `${approvalRate}%`;
  const latencyValue =
    user?.isDemo && latencyDays === null ? "1.2d" : latencyDays === null ? "—" : `${latencyDays.toFixed(1)}d`;

  const calendarEvents = workLogEvents(filteredLogs as any[], (l: any) => studentNameOf(l));

  return (
    <div className="space-y-6 pb-4">
      <PageHeader
        title="Work log governance"
        subtitle="Consolidated oversight of student daily activities, supervisor approvals, and hourly compliance."
        breadcrumbs={[{ label: "Dashboard", path: "/admin/dashboard" }, { label: "Work Logs" }]}
        action={
          <Button>
            <Download />
            Export log data
          </Button>
        }
      />

      {/* Quick Insights */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 md:gap-4">
        <StatCard
          label="Total hours captured"
          value={user?.isDemo && totalHours === 0 ? 384200 : totalHours}
          icon={<Clock />}
        />
        <StatCard label="Approval rate" value={qualityValue} icon={<FileCheck2 />} />
        <StatCard label="Review latency" value={latencyValue} icon={<CalendarDays />} />
      </div>

      {!isLoading && (
        <ActivityCalendar
          title="Activity calendar"
          events={calendarEvents}
          legend={workLogLegend(filteredLogs as any[])}
          asideTitle="Latest submissions"
          aside={
            filteredLogs.length === 0 ? (
              <p className="text-sm text-muted-foreground">No logs match the current filters.</p>
            ) : (
              <div className="space-y-2.5">
                {latestLogs(filteredLogs as any[]).map((log: any) => (
                  <CalendarAsideCard
                    key={log.id}
                    tone={log.status === "changes_requested" ? "lilac" : toneForStatus(log.status)}
                    title={studentNameOf(log)}
                    meta={`${partnerNameOf(log)} · ${log.hours ?? 0}h`}
                    badge={relativeDay(log.date)}
                  />
                ))}
              </div>
            )
          }
        />
      )}

      <SectionCard
        title="Platform-wide logs"
        actions={
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Search logs…"
              className="min-w-0 basis-full sm:w-60 sm:basis-auto"
            />
            <Select value={partnerFilter} onValueChange={setPartnerFilter}>
              <SelectTrigger className="min-w-0 flex-1 sm:w-[180px] sm:flex-none" aria-label="Partner">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All partners</SelectItem>
                {partnerOptions.map((name: string) => (
                  <SelectItem key={name} value={name}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="min-w-0 flex-1 sm:w-[160px] sm:flex-none" aria-label="Status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All status</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="changes_requested">Changes requested</SelectItem>
              </SelectContent>
            </Select>
          </div>
        }
        noPadding
      >
        {isLoading ? (
          <TableSkeleton rows={5} columns={4} />
        ) : filteredLogs.length === 0 ? (
          <EmptyState
            bare
            icon={<NotebookPen />}
            title="No work logs"
            description="No logs match the current search and filters."
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="pl-4 md:pl-5">Student</TableHead>
                <TableHead>Partner organization</TableHead>
                <TableHead>Activity date</TableHead>
                <TableHead className="text-right">Hours</TableHead>
                <TableHead className="pr-4 text-right md:pr-5">Governance</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLogs.map((log: any) => (
                <TableRow key={log.id}>
                  <TableCell className="min-w-[180px] pl-4 md:pl-5">
                    <div className="flex items-center gap-2.5">
                      <Avatar className="size-8">
                        <AvatarFallback className="bg-pastel-lavender text-caption text-pastel-lavender-ink">
                          {studentNameOf(log).charAt(0) || "U"}
                        </AvatarFallback>
                      </Avatar>
                      <span className="font-medium text-foreground">{studentNameOf(log)}</span>
                    </div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-body">{partnerNameOf(log)}</TableCell>
                  <TableCell className="whitespace-nowrap text-muted-foreground tabular-nums">
                    {formatDate(log.date)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-foreground tabular-nums">
                    {log.hours}h
                  </TableCell>
                  <TableCell className="pr-4 text-right md:pr-5">
                    <StatusChip status={log.status} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </SectionCard>
    </div>
  );
}
